
/**
 * Utility functions to handle tracking scripts and event tracking
 * Version 2.2 - Last Updated: 4/16/2025
 */

/**
 * Check if HubSpot Forms script is loaded and ready to use
 * @returns boolean indicating whether HubSpot forms script is fully loaded
 */
export const isHubSpotFormsLoaded = (): boolean => {
  return !!(window.hbspt && window.hbspt.forms);
};

/**
 * Load the HubSpot form script on demand
 * This loads the forms script specifically, not the tracking script
 * @returns Promise that resolves when the script is loaded or rejects on error
 */
export const loadHubSpotFormsScript = (): Promise<void> => {
  return new Promise((resolve, reject) => {
    try {
      // If script is already loaded and functional, resolve immediately
      if (isHubSpotFormsLoaded()) {
        console.log('HubSpot forms script already loaded and functional');
        resolve();
        return;
      }
      
      console.log("Loading HubSpot forms script...");
      
      // Check if script is loading or already exists
      const scriptId = 'hubspot-forms-script';
      let existingScript = document.getElementById(scriptId) as HTMLScriptElement | null;
      
      // If script exists but is in error state, remove it
      if (existingScript && existingScript.dataset.loadStatus === 'error') {
        existingScript.remove();
        existingScript = null;
      }
      
      // If script is already loading, wait for it
      if (existingScript && existingScript.dataset.loadStatus === 'loading') {
        // Set up event listeners for existing script
        existingScript.addEventListener('load', () => {
          existingScript?.setAttribute('data-load-status', 'ready');
          console.log('HubSpot forms script loaded via existing script');
          
          // Add a small delay to ensure everything is initialized
          setTimeout(() => {
            if (window.hbspt && window.hbspt.forms) {
              resolve();
            } else {
              reject(new Error('HubSpot forms API not available after script loaded'));
            }
          }, 500);
        });
        
        existingScript.addEventListener('error', () => {
          existingScript?.setAttribute('data-load-status', 'error');
          console.error('Failed to load HubSpot forms script (existing script)');
          reject(new Error('Failed to load HubSpot script'));
        });
        
        // Set a timeout in case the script hangs
        setTimeout(() => {
          if (existingScript?.dataset.loadStatus === 'loading') {
            reject(new Error('HubSpot script loading timed out'));
          }
        }, 5000);
        
        return;
      }
      
      // Create a new script if none exists or the existing one failed
      const script = document.createElement('script');
      script.id = scriptId;
      script.src = 'https://js.hsforms.net/forms/v2.js'; // Standard HubSpot forms script
      script.async = true;
      script.defer = true;
      script.setAttribute('data-load-status', 'loading');
      
      script.onload = () => {
        script.setAttribute('data-load-status', 'ready');
        console.log('HubSpot forms script loaded successfully');
        
        // Add a small delay to ensure everything is initialized
        setTimeout(() => {
          if (window.hbspt && window.hbspt.forms) {
            console.log('HubSpot forms API is available');
            resolve();
          } else {
            console.error('HubSpot forms API not available after script loaded');
            script.setAttribute('data-load-status', 'error');
            
            // Try alternative script URL as fallback
            const fallbackScript = document.createElement('script');
            fallbackScript.id = 'hubspot-forms-script-fallback';
            fallbackScript.src = 'https://js-eu1.hsforms.net/forms/embed/v2.js'; // Alternative URL
            fallbackScript.async = true;
            fallbackScript.defer = true;
            
            fallbackScript.onload = () => {
              console.log('HubSpot forms fallback script loaded successfully');
              if (window.hbspt && window.hbspt.forms) {
                resolve();
              } else {
                reject(new Error('HubSpot forms API not available after fallback script loaded'));
              }
            };
            
            fallbackScript.onerror = () => {
              reject(new Error('Failed to load HubSpot fallback script'));
            };
            
            document.head.appendChild(fallbackScript);
          }
        }, 500);
      };
      
      script.onerror = () => {
        script.setAttribute('data-load-status', 'error');
        console.error('Failed to load HubSpot script');
        
        // Try alternative script URL as fallback
        const fallbackScript = document.createElement('script');
        fallbackScript.id = 'hubspot-forms-script-fallback';
        fallbackScript.src = 'https://js-eu1.hsforms.net/forms/embed/v2.js';
        fallbackScript.async = true;
        fallbackScript.defer = true;
        
        fallbackScript.onload = () => {
          console.log('HubSpot forms fallback script loaded successfully');
          if (window.hbspt && window.hbspt.forms) {
            resolve();
          } else {
            reject(new Error('HubSpot forms API not available after fallback script loaded'));
          }
        };
        
        fallbackScript.onerror = () => {
          reject(new Error('Failed to load HubSpot fallback script'));
        };
        
        document.head.appendChild(fallbackScript);
      };
      
      // Add the script to the document
      document.head.appendChild(script);
      
    } catch (error) {
      console.error('Error in loadHubSpotFormsScript:', error);
      reject(error);
    }
  });
};

/**
 * Track a page view event in analytics platforms (Google Analytics only)
 * @param url The URL to track
 * @param title The page title
 */
export const trackPageView = (url: string, title: string): void => {
  try {
    // Track in Google Tag Manager
    if (window.dataLayer) {
      window.dataLayer.push({
        event: 'virtual-page-view',
        page: {
          path: url,
          title: title
        }
      });
    }
    
    // Track in Google Analytics via gtag directly as a fallback
    if (typeof window.gtag === 'function') {
      window.gtag('config', 'G-453GTN6HBF', {
        page_path: url,
        page_title: title
      });
    }
    
    console.log('Page view tracked:', url);
  } catch (error) {
    console.error('Error tracking page view:', error);
  }
};

// HubSpot tracking functionality removed - only forms are used

/**
 * Track a custom event in analytics platforms (Google Analytics only)
 * @param eventName Name of the event
 * @param eventData Additional data for the event
 */
export const trackEvent = (eventName: string, eventData: Record<string, any> = {}): void => {
  try {
    // Track in Google Tag Manager
    if (window.dataLayer) {
      window.dataLayer.push({
        event: eventName,
        ...eventData
      });
    }
    
    // Track in Google Analytics via gtag directly
    if (typeof window.gtag === 'function') {
      window.gtag('event', eventName, eventData);
    }
    
    console.log('Event tracked:', eventName, eventData);
  } catch (error) {
    console.error('Error tracking event:', error);
  }
};
